import { useEffect, useState } from "react";
import { 
  FileText, 
  Calendar, 
  MessageSquare, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = ({ view = "dashboard" }) => {
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [view]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (view === "dashboard" || view === "quotes" || view === "bookings") {
        const [dashRes, quotesRes] = await Promise.all([
          axios.get(`${API}/admin/dashboard`, { headers }),
          axios.get(`${API}/quotes`, { headers })
        ]);
        setStats(dashRes.data.stats);
        setQuotes(quotesRes.data);
      }
      if (view === "contacts") {
        const contactsRes = await axios.get(`${API}/contacts`, { headers });
        setContacts(contactsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.patch(`${API}/quotes/${quoteId}/status?status=${status}`, {}, { headers });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: "status-badge status-pending",
      contacted: "status-badge bg-purple-100 text-purple-800",
      booked: "status-badge status-booked",
      completed: "status-badge status-completed",
      cancelled: "status-badge status-cancelled",
      outside_area: "status-badge bg-gray-100 text-gray-800"
    };
    return <span className={statusClasses[status] || statusClasses.pending}>{status}</span>;
  };

  const statCards = [
    { icon: FileText, label: "Total Quotes", value: stats?.total_quotes || 0, color: "bg-blue-500" },
    { icon: Clock, label: "Pending", value: stats?.pending_quotes || 0, color: "bg-yellow-500" },
    { icon: Calendar, label: "Booked", value: stats?.booked_quotes || 0, color: "bg-green-500" },
    { icon: CheckCircle, label: "Completed", value: stats?.completed_quotes || 0, color: "bg-purple-500" }
  ];

  const filteredQuotes = view === "bookings" 
    ? quotes.filter(q => q.status === "booked" && q.booking_date)
    : quotes;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card">
              <div className="h-8 skeleton rounded w-1/3 mb-2" />
              <div className="h-4 skeleton rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      {/* Dashboard View */}
      {view === "dashboard" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {statCards.map((stat, i) => (
              <div key={i} className="stat-card" data-testid={`stat-${stat.label.toLowerCase().replace(" ", "-")}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-value">{stat.value}</p>
                    <p className="stat-label">{stat.label}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Quotes */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Recent Quotes</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Project</th>
                    <th>Location</th>
                    <th>Estimate</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.slice(0, 5).map((quote) => (
                    <tr key={quote.id}>
                      <td className="font-medium">{quote.name}</td>
                      <td className="capitalize">{quote.project_type?.replace("_", " ")}</td>
                      <td>{quote.postcode}</td>
                      <td>£{quote.estimated_min} - £{quote.estimated_max}</td>
                      <td>{getStatusBadge(quote.status)}</td>
                      <td className="text-gray-500">{new Date(quote.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Quotes View */}
      {(view === "quotes" || view === "bookings") && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              {view === "bookings" ? "Booked Appointments" : "All Quotes"}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Project</th>
                  <th>Location</th>
                  <th>Estimate</th>
                  {view === "bookings" && <th>Appointment</th>}
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((quote) => (
                  <tr key={quote.id}>
                    <td className="font-medium">{quote.name}</td>
                    <td>
                      <div className="text-sm">
                        <p>{quote.email}</p>
                        <p className="text-gray-500">{quote.phone}</p>
                      </div>
                    </td>
                    <td className="capitalize">{quote.project_type?.replace("_", " ")}</td>
                    <td>{quote.postcode}</td>
                    <td>£{quote.estimated_min} - £{quote.estimated_max}</td>
                    {view === "bookings" && (
                      <td>
                        <div className="text-sm">
                          <p className="font-medium">{quote.booking_date}</p>
                          <p className="text-gray-500">{quote.booking_time}</p>
                        </div>
                      </td>
                    )}
                    <td>{getStatusBadge(quote.status)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedQuote(quote)}
                          data-testid={`view-quote-${quote.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Select
                          value={quote.status}
                          onValueChange={(value) => updateQuoteStatus(quote.id, value)}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Contacts View */}
      {view === "contacts" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Contact Submissions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Message</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id}>
                    <td className="font-medium">{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.phone}</td>
                    <td className="max-w-xs truncate">{contact.message}</td>
                    <td className="text-gray-500">{new Date(contact.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quote Detail Dialog */}
      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Quote Details</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{selectedQuote.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedQuote.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedQuote.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedQuote.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Project Type</p>
                  <p className="font-medium capitalize">{selectedQuote.project_type?.replace("_", " ")}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Room Size</p>
                  <p className="font-medium capitalize">{selectedQuote.room_size}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tile Type</p>
                  <p className="font-medium capitalize">{selectedQuote.tile_type?.replace("_", " ")}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Postcode</p>
                  <p className="font-medium">{selectedQuote.postcode}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Estimate</p>
                  <p className="font-medium text-lg">£{selectedQuote.estimated_min} - £{selectedQuote.estimated_max}</p>
                </div>
                {selectedQuote.booking_date && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Appointment</p>
                    <p className="font-medium">{selectedQuote.booking_date} at {selectedQuote.booking_time}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
